import { isTaskPriority, type TaskPriority } from "@/features/tasks/lib/priority";
import { isTaskStatus, type TaskStatus } from "@/features/tasks/lib/board";

export const TASK_SORTS = ["updated", "due", "priority", "created", "number"] as const;
export type TaskSort = (typeof TASK_SORTS)[number];
export const DEFAULT_SORT: TaskSort = "updated";

/** Who the list is filtered to: me, nobody, or one person's id. */
export const ASSIGNEE_ME = "me";
export const ASSIGNEE_NONE = "none";

/** The task list's filters, as they live in the URL so a filtered list can be shared. */
export interface TaskListFilters {
  /** Empty means every status except cancelled. */
  statuses: TaskStatus[];
  assignee: string | null;
  priorities: TaskPriority[];
  dueAfter: string | null;
  dueBefore: string | null;
  q: string;
  sort: TaskSort;
  /** Label ids: tasks carrying any of them. */
  labels: string[];
  /** A milestone id, MILESTONE_NONE for tasks outside any milestone, or null for all. */
  milestone: string | null;
  /** A phase id, PHASE_NONE for tasks in no phase, or null for all. */
  phase: string | null;
  /** Only tasks still waiting for an open blocker. */
  blocked: boolean;
}

/** The milestone filter value for tasks that belong to no milestone. */
export const MILESTONE_NONE = "none";

/** The phase filter value for tasks that sit in no phase. */
export const PHASE_NONE = "none";

const PARAM = {
  status: "status",
  assignee: "assignee",
  priority: "priority",
  dueAfter: "due_after",
  dueBefore: "due_before",
  q: "q",
  sort: "sort",
  labels: "label",
  milestone: "milestone",
  phase: "phase",
  blocked: "blocked",
} as const;

/** How a switched-on flag reads in the URL (?blocked=1). */
const FLAG_ON = "1";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const SEPARATOR = ",";

/** Every value of a list parameter, written either as status=a,b or status=a&status=b. */
function listOf(params: URLSearchParams, name: string): string[] {
  return params
    .getAll(name)
    .flatMap((value) => value.split(SEPARATOR))
    .filter(Boolean);
}

function dateOf(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  return value !== null && ISO_DATE.test(value) ? value : null;
}

function isTaskSort(value: string | null): value is TaskSort {
  return value !== null && (TASK_SORTS as readonly string[]).includes(value);
}

/** Read the filters from the URL; anything unknown is ignored. */
export function parseListParams(params: URLSearchParams): TaskListFilters {
  const sort = params.get(PARAM.sort);
  return {
    statuses: [...new Set(listOf(params, PARAM.status).filter(isTaskStatus))],
    assignee: params.get(PARAM.assignee) || null,
    priorities: [...new Set(listOf(params, PARAM.priority).filter(isTaskPriority))],
    dueAfter: dateOf(params, PARAM.dueAfter),
    dueBefore: dateOf(params, PARAM.dueBefore),
    q: params.get(PARAM.q)?.trim() ?? "",
    sort: isTaskSort(sort) ? sort : DEFAULT_SORT,
    labels: [...new Set(listOf(params, PARAM.labels))],
    milestone: params.get(PARAM.milestone) || null,
    phase: params.get(PARAM.phase) || null,
    blocked: params.get(PARAM.blocked) === FLAG_ON,
  };
}

function setOrDelete(params: URLSearchParams, name: string, value: string | null): void {
  if (value) params.set(name, value);
  else params.delete(name);
}

/** The URL parameters for the filters. Other parameters (such as ?task=) are kept. */
export function writeListParams(
  current: URLSearchParams,
  filters: TaskListFilters,
): URLSearchParams {
  const next = new URLSearchParams(current);
  setOrDelete(next, PARAM.status, filters.statuses.join(SEPARATOR));
  setOrDelete(next, PARAM.assignee, filters.assignee);
  setOrDelete(next, PARAM.priority, filters.priorities.join(SEPARATOR));
  setOrDelete(next, PARAM.dueAfter, filters.dueAfter);
  setOrDelete(next, PARAM.dueBefore, filters.dueBefore);
  setOrDelete(next, PARAM.q, filters.q.trim());
  setOrDelete(next, PARAM.sort, filters.sort === DEFAULT_SORT ? null : filters.sort);
  setOrDelete(next, PARAM.labels, filters.labels.join(SEPARATOR));
  setOrDelete(next, PARAM.milestone, filters.milestone);
  setOrDelete(next, PARAM.phase, filters.phase);
  setOrDelete(next, PARAM.blocked, filters.blocked ? FLAG_ON : null);
  return next;
}

/** Whether anything narrows the list (the sort does not). */
export function hasActiveFilters(filters: TaskListFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.assignee !== null ||
    filters.priorities.length > 0 ||
    filters.dueAfter !== null ||
    filters.dueBefore !== null ||
    filters.q !== "" ||
    filters.labels.length > 0 ||
    filters.milestone !== null ||
    filters.phase !== null ||
    filters.blocked
  );
}

/** The same filters with nothing narrowing the list, keeping the sort. */
export function clearedFilters(filters: TaskListFilters): TaskListFilters {
  return { ...parseListParams(new URLSearchParams()), sort: filters.sort };
}

const OPEN_AND_DONE: readonly TaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
];

/** The search request (GET /tasks) for a project's list. */
export function searchQueryOf(projectKey: string, filters: TaskListFilters) {
  return {
    project: [projectKey],
    status: filters.statuses.length > 0 ? filters.statuses : [...OPEN_AND_DONE],
    assignee: filters.assignee ? [filters.assignee] : null,
    priority: filters.priorities.length > 0 ? filters.priorities : null,
    due_after: filters.dueAfter,
    due_before: filters.dueBefore,
    q: filters.q || null,
    sort: filters.sort,
    label: filters.labels.length > 0 ? filters.labels : null,
    milestone: filters.milestone,
    phase: filters.phase,
    blocked: filters.blocked || undefined,
  };
}
